import {
  MARK_WON_ADMIN_FROM_STATES,
  effectiveRelayStatus,
  formatDeliveryAddress,
} from '@carsfinders/contracts';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams, useSearch } from '@tanstack/react-router';
import { ArrowLeft, Check } from 'lucide-react';
import { useState } from 'react';

import { AccessDenied } from '../../components/access-denied';
import { ConfirmDialog } from '../../components/confirm-dialog';
import { ApiError } from '../../lib/api';
import { t } from '../../lib/i18n';
import { toast } from '../../lib/toast';
import { UserAvatar } from '../clients/components/user-avatar';
import {
  FINANCE_CLIENTS_QUERY_KEY,
  PRICING_RULES_QUERY_KEY,
  clientInvoicesQueryKey,
  fetchFinanceClients,
  fetchPricingRules,
} from '../finance/api';
import { IssueInvoiceDrawer } from '../finance/components/issue-invoice-drawer';
import { initialsOf } from '../clients/utils';
import { DEALS_QUEUE_QUERY_KEY } from '../deals/api';
import { fetchBidDetail, fetchBids } from './api';
import { AddToLedgerSection } from './components/add-to-ledger-section';
import { BidHistoryTab } from './components/bid-history-tab';
import { BidsTable } from './components/bids-table';
import { BidPaymentSection } from './components/bid-payment-section';
import { BidStatusBadge } from './components/bid-status-badge';
import { LotThumbnail } from './components/lot-thumbnail';
import { MinimumBidControl } from './components/minimum-bid-control';
import { useMirrorBid } from './hooks/use-mirror-bid';
import {
  BIDS_QUERY_KEY,
  useCancelAllBids,
  useSetBidProcessed,
  useUpdateBidStatus,
} from './hooks/use-bid-mutations';
import {
  type BidDetailCar,
  BidStatus,
  type BidUser,
  dealPaymentStanding,
  isFinishedBid,
  isMirrorableOnAuction,
  isProcessedOnAuction,
  type WinnerReplacementConfirmation,
} from './types';
import {
  bidderLabel,
  asWinnerConfirmation,
  cn,
  formatAmount,
  formatChf,
  formatClock,
  formatShortDate,
  lotSiteHref,
  auctionEndOf,
  AUCTION_END_TONE_CLASS,
  isAuctionRunning,
} from './utils';

type BidDetailTab = 'details' | 'history';
const BID_DETAIL_TABS: readonly BidDetailTab[] = ['details', 'history'];

function carName(car: BidDetailCar | null): string {
  if (!car) return '—';
  const name =
    `${car.ai_brand ?? car.brand ?? car.make ?? ''} ${car.ai_model ?? car.model ?? ''}`.trim();
  return name || car.listing_id || '—';
}

/**
 * The lot's REAL auction close — `auction_end_date` (the BSON Date ingest
 * writes and soft close moves), else the legacy `end_date` string older lots
 * still carry; the same order as the deals queue's `auctionEndAtOf`. Reading
 * only `end_date` made every ingested lot look closed (owner, 2026-10-12): no
 * close time on the page, and «Виграно / Не виграно» live mid-auction.
 */
export function auctionEndRaw(car: BidDetailCar | null): string | null {
  return car?.auction_end_date || car?.end_date || null;
}

/**
 * True while the lot's auction is still running, as far as the bid detail
 * knows (owner, 2026-10-11: «Виграно / Не виграно» are only meaningful once
 * the real auction has closed — before that the operator cannot know the
 * outcome, and a premature «Виграно» mints a deal). A lot without a known end
 * (car document gone, or never carried one) reads as closed: the operator
 * must still be able to decide such a bid.
 */
export function isAuctionStillOpen(car: BidDetailCar | null, now: number = Date.now()): boolean {
  return isAuctionRunning(auctionEndRaw(car), now);
}

/**
 * Single bid (/bids/$id) — the operator's full view of one client bid.
 *
 * Design D groups it as three cards: the facts, the per-bid operator actions,
 * and below them the two LOT-scoped levers, which act on the car rather than on
 * this bid and are audited server-side (`admin.minimum_bid_set`,
 * `admin.bids_cancelled`). The separation is not cosmetic — mixing a "this bid"
 * button with a "every bid on this lot" button in one row is how an operator
 * voids a whole lot meaning to reject one person.
 */
export function BidDetailPage() {
  const { id } = useParams({ strict: false }) as { id?: string };
  // `?from=client` — the client page's «Ставки» rows write it; read up here so
  // the hook order never depends on the access check below.
  const search = useSearch({ strict: false }) as { from?: unknown };
  const statusMutation = useUpdateBidStatus();
  const processedMutation = useSetBidProcessed();
  const cancelAllMutation = useCancelAllBids();

  /** Set when api-node answers 409: the lot already has a winner. */
  const [replaceWinner, setReplaceWinner] = useState<WinnerReplacementConfirmation | null>(null);
  const [confirmCancelAll, setConfirmCancelAll] = useState(false);
  /** Phase 13: «Деталі» (the three cards) or «Історія» (the audit trail of this bid). */
  const [tab, setTab] = useState<BidDetailTab>('details');

  const detailQuery = useQuery({
    queryKey: ['bids', 'detail', id],
    queryFn: () => fetchBidDetail(id as string),
    enabled: Boolean(id),
  });

  // «Виставити рахунок» for a won bid whose deal is not invoiced yet (the
  // «Оплата» section offers it) — the same drawer «Угоди» opens, fed the same
  // two queries only while it is open.
  const queryClient = useQueryClient();
  /**
   * «Оброблено» on an open, unplaced bid places it (owner, 2026-10-25): the
   * shared claim → confirm-relay flow; the server writes the mark with the flip.
   */
  const mirror = useMirrorBid(async () => {
    await detailQuery.refetch();
    void queryClient.invalidateQueries({ queryKey: DEALS_QUEUE_QUERY_KEY });
  });
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const clientsQuery = useQuery({
    queryKey: FINANCE_CLIENTS_QUERY_KEY,
    queryFn: fetchFinanceClients,
    enabled: invoiceOpen,
  });
  const rulesQuery = useQuery({
    queryKey: PRICING_RULES_QUERY_KEY,
    queryFn: fetchPricingRules,
    enabled: invoiceOpen,
  });
  const closeInvoice = () => {
    setInvoiceOpen(false);
    // An issued invoice moves the deal past `won`: the section swaps the preview
    // for the stored invoice, so both the bid and the client's invoices refetch.
    void queryClient.invalidateQueries({ queryKey: ['bids', 'detail', id] });
    if (detailQuery.data) {
      void queryClient.invalidateQueries({
        queryKey: clientInvoicesQueryKey(detailQuery.data.bid.userId),
      });
    }
  };

  const error = detailQuery.error;
  if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
    return <AccessDenied />;
  }

  /**
   * Winner-replacement handshake: the first attempt goes out with
   * forceReplace=false; a 409 means the lot already has a winner and the
   * operator must confirm before we re-issue with forceReplace=true. Without
   * this the admin SPA could never replace a winner — it hardcoded false and
   * surfaced the 409 as a generic failure.
   */
  const changeStatus = async (status: BidStatus, forceReplace = false) => {
    if (!id || statusMutation.isPending) return;
    try {
      await statusMutation.mutateAsync({ id, status, forceReplace });
      setReplaceWinner(null);
      await detailQuery.refetch();
      toast.success(t('bids.management.statusUpdateSuccess'));
    } catch (mutationError) {
      const confirmation = asWinnerConfirmation(mutationError);
      if (confirmation) {
        setReplaceWinner(confirmation);
        return;
      }
      toast.error(t('bids.management.statusUpdateError'));
    }
  };

  const cancelAllBids = async () => {
    const carId = detailQuery.data?.car?._id ?? detailQuery.data?.bid.carId;
    if (!carId || cancelAllMutation.isPending) return;
    try {
      const result = await cancelAllMutation.mutateAsync({ carId });
      setConfirmCancelAll(false);
      await detailQuery.refetch();
      toast.success(
        result.cancelledCount > 0
          ? t('bidDetails.success.cancelAll')
          : t('bidDetails.info.allAlreadyCancelled'),
      );
    } catch {
      setConfirmCancelAll(false);
      toast.error(t('bidDetails.errors.cancelAll'));
    }
  };

  const toggleProcessed = async () => {
    if (!id || !detail || processedMutation.isPending) return;
    // A Won / relayed bid is processed by definition — nothing to toggle.
    if (isProcessedOnAuction(detail.bid).derived) return;
    if (isMirrorableOnAuction(detail.bid)) {
      await mirror.start(detail.bid._id, detail.bid.bid);
      return;
    }
    try {
      await processedMutation.mutateAsync({ id, processed: !detail.bid.processedOnAuction });
      await detailQuery.refetch();
    } catch {
      toast.error(t('bids.management.statusUpdateError'));
    }
  };

  // `?from=client` (written by the client page's «Ставки» rows): «Назад» returns
  // to THAT client's bids tab, not to the deals list — the operator was reading
  // one client's history, not working the desk (owner, 2026-10-11). The owner
  // is read off the loaded bid; until it loads the link falls back to the desk.
  const backToClientId = search.from === 'client' ? detailQuery.data?.bid.userId : undefined;
  const backLinkClass =
    'inline-flex w-fit items-center gap-1.5 text-[13px] font-semibold text-cf-ink-muted hover:text-cf-ink';
  const backLink = backToClientId ? (
    <Link
      to="/clients/$id"
      params={{ id: backToClientId }}
      search={{ tab: 'bids' }}
      data-testid="bid-back-link"
      className={backLinkClass}
    >
      <ArrowLeft size={16} strokeWidth={1.75} />
      {t('bids.dAdmin.detailPage.backToClient')}
    </Link>
  ) : (
    <Link to="/deals" data-testid="bid-back-link" className={backLinkClass}>
      <ArrowLeft size={16} strokeWidth={1.75} />
      {t('bids.dAdmin.detailPage.back')}
    </Link>
  );

  if (detailQuery.isLoading) {
    return (
      <>
        {backLink}
        <p className="rounded-cf-lg border border-cf-line bg-cf-surface px-4 py-10 text-center text-[13px] text-cf-ink-subtle">
          {t('common.loading.data')}
        </p>
      </>
    );
  }

  const detail = detailQuery.data;
  if (detailQuery.isError || !detail) {
    return (
      <>
        {backLink}
        <section className="rounded-cf-lg border border-cf-line bg-cf-surface p-10 text-center">
          <p className="text-lg font-semibold">{t('bids.dAdmin.detailPage.notFound')}</p>
        </section>
      </>
    );
  }

  const { bid, car, user } = detail;
  const processed = isProcessedOnAuction(bid);
  const finished = isFinishedBid(bid);
  const auctionOpen = isAuctionStillOpen(car);
  const auctionEnd = auctionEndOf(auctionEndRaw(car));
  /** «Виграно / Не виграно» exist only for a bid that still leads. */
  const decidable = bid.status !== BidStatus.Outbid;
  /**
   * A winner the operator replaced (BUG-11): Not Won, but the relay still
   * reads `won` — a demoted winner keeps it (decision D-7). The only action
   * left is putting it back; the 409 handshake asks before the swap. A Not Won
   * bid with any other relay state was never the upstream winner.
   */
  const restorable = bid.status === BidStatus.Cancelled && bid.relayStatus === 'won';
  /**
   * BUG-01 (e2e-bugfix-2, D-1): every OTHER Not Won bid — relay cancelled,
   * lost, upstream_rejected, or never relayed at all — was not the upstream
   * winner, yet the real auction may still award it the lot (the operator
   * mirrored the wrong bid; the top bidder defaulted and the lot was
   * re-awarded). The operator records that from here with «Призначити
   * переможцем»: the same PATCH Won and the same 409 handshake as the restore —
   * the server, not this page, decides whether a winner is being replaced.
   * Only from the relay states mark-won itself accepts
   * (`MARK_WON_ADMIN_FROM_STATES`): anything else — lost, upstream_rejected,
   * expired — answers 409 INVALID_RELAY_TRANSITION with no confirmation to
   * give, so the button would be a dead end. Legacy bids without a relay
   * state normalise to `captured` and get the button on purpose.
   */
  const assignable =
    bid.status === BidStatus.Cancelled &&
    bid.relayStatus !== 'won' &&
    MARK_WON_ADMIN_FROM_STATES.includes(effectiveRelayStatus(bid.relayStatus));
  /**
   * An ADMIN's own win (local-ledger bridge): the owners bought the car for
   * themselves, so it goes to their ledger, never to an invoice. While its deal
   * is still uninvoiced the page offers «Облік» instead of «Оплата» — the same
   * rule the «Угоди» row follows (`resolveDealStage`).
   */
  const ownWin = bid.status === BidStatus.Won && detail.bidderIsAdmin === true;
  const ledgerInsteadOfInvoice = ownWin && (!detail.deal || detail.deal.state === 'won');
  const payment =
    bid.status === BidStatus.Won && detail.deal && !ledgerInsteadOfInvoice
      ? dealPaymentStanding(detail.deal.state)
      : null;
  /** Lot the bid belongs to — the scope of the two car-level admin controls. */
  const lotId = car?._id ?? bid.carId;
  // «First Last» before the chosen handle — the same rule as the bids list.
  const bidderName = bidderLabel({ user, userId: bid.userId });
  const deliveryAddress = formatDeliveryAddress(user?.deliveryAddress);

  return (
    <>
      {backLink}

      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3 lg:gap-4">
          {/*
            Photo + title open the car on the auction site, in a new tab (owner,
            2026-10-18 — the admin's own lot page is gone). The photo is
            decorative inside the link: the title already names the car.
          */}
          <a
            href={lotSiteHref({ carId: bid.carId, listingId: car?.listing_id })}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="bid-detail-lot-link"
            aria-label={t('bids.dAdmin.openLotAria', { lot: carName(car) })}
            className="flex min-w-0 items-center gap-3 rounded-cf-md hover:underline focus-visible:outline-2 focus-visible:outline-cf-brand lg:gap-4"
          >
            <LotThumbnail src={detail.carImageUrl} size="lg" />
            <h1 className="min-w-0 text-[26px] leading-[1.1] font-semibold tracking-[-0.02em] lg:text-[30px]">
              {carName(car)}
            </h1>
          </a>
        </div>
        <div className="flex items-center gap-1.5">
          <BidStatusBadge status={bid.status} />
          {payment && (
            <span
              data-testid="bid-payment-standing"
              data-standing={payment}
              className={cn(
                'inline-flex rounded-cf-sm px-2 py-1 font-mono text-[10px] tracking-[0.08em] uppercase',
                payment === 'paid'
                  ? 'bg-cf-success-tint text-cf-success'
                  : payment === 'forfeited'
                    ? 'bg-cf-danger-tint text-cf-danger'
                    : 'bg-cf-warn-tint text-cf-warn',
              )}
            >
              {t(`bids.dAdmin.detailPage.payment.${payment}`)}
            </span>
          )}
        </div>
      </div>

      {/* ── Tabs (Phase 13) ─────────────────────────────────────────────── */}
      <div
        role="tablist"
        className="flex w-fit gap-0.5 rounded-cf-lg bg-cf-fill p-[3px]"
        data-testid="bid-detail-tabs"
      >
        {BID_DETAIL_TABS.map((key) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            onClick={() => setTab(key)}
            className={cn(
              'h-9 rounded-cf-md px-3.5 text-[13px] font-semibold whitespace-nowrap',
              tab === key ? 'bg-cf-surface text-cf-brand' : 'bg-transparent text-cf-ink-muted',
            )}
          >
            {t(`bids.dAdmin.detailPage.tabs.${key}`)}
          </button>
        ))}
      </div>

      {tab === 'history' && id && <BidHistoryTab bidId={id} />}

      {tab === 'details' && (
        <>
      {/* ── Facts ───────────────────────────────────────────────────────── */}
      <section className="rounded-cf-lg border border-cf-line bg-cf-surface px-4 py-4 lg:px-[22px] lg:py-5">
        <h2 className="font-mono text-[10px] tracking-[0.14em] text-cf-ink-subtle uppercase">
          {t('bids.dAdmin.detailPage.bidSection')}
        </h2>
        <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-3.5 lg:grid-cols-3">
          <Field label={t('bids.detail.amount')} value={`CHF ${formatAmount(bid.bid)}`} mono />
          <ClientField userId={bid.userId} user={user} bidderName={bidderName} />
          <Field
            label={t('bids.dAdmin.columns.date')}
            value={`${formatShortDate(bid.createdAt)} ${formatClock(bid.createdAt)}`.trim()}
            mono
          />
          <Field label={t('bids.dAdmin.detailPage.phone')} value={user?.phone ?? '—'} />
          {/* Owner, 2026-10-11: every car is delivered to the company's own yard,
              so a client who entered nothing is shown that destination, not
              «не вказано». A client-entered address still wins when present. */}
          <Field
            label={t('bids.dAdmin.detailPage.deliveryAddress')}
            value={deliveryAddress || t('bids.dAdmin.detailPage.deliveryAddressDefault')}
          />
          {car?.listing_id && <Field label={t('bids.detail.listing')} value={car.listing_id} mono />}
          {car?.auction_name && <Field label={t('bids.detail.auction')} value={car.auction_name} />}
          {/* Owner, 2026-10-12: «я маю бачити коли ставка закривається». */}
          {auctionEnd && (
            <Field
              label={t('bids.dAdmin.detailPage.auctionEnd')}
              value={auctionEnd.label}
              valueClassName={AUCTION_END_TONE_CLASS[auctionEnd.tone]}
              testId="bid-auction-end"
            />
          )}
        </dl>
      </section>

      {/* ── Won → «Оплата»: the preview before issuing, the invoice after ── */}
      {bid.status === BidStatus.Won && detail.deal && !ledgerInsteadOfInvoice && (
        <BidPaymentSection
          userId={bid.userId}
          deal={detail.deal}
          onIssueInvoice={() => setInvoiceOpen(true)}
        />
      )}

      {/* ── Won by an admin → «Облік»: the card in the owners' local ledger ── */}
      {ownWin && (
        <AddToLedgerSection
          bidId={bid._id}
          localVehicleId={detail.localVehicleId ?? null}
          onAdded={() => {
            void queryClient.invalidateQueries({ queryKey: ['bids', 'detail', id] });
            void queryClient.invalidateQueries({ queryKey: DEALS_QUEUE_QUERY_KEY });
          }}
        />
      )}

      {/* ── A closed bid: restore a replaced winner, or assign it the lot — nothing else ── */}
      {(restorable || assignable) && (
        <section className="rounded-cf-lg border border-cf-line bg-cf-surface px-4 py-4 lg:px-[22px] lg:py-5">
          <h2 className="font-mono text-[10px] tracking-[0.14em] text-cf-ink-subtle uppercase">
            {t('bids.dAdmin.detailPage.operatorSection')}
          </h2>
          {assignable && (
            <p className="mt-1.5 text-[13px] text-cf-ink-muted">
              {t('bids.management.actions.assignWinnerHint')}
            </p>
          )}
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => void changeStatus(BidStatus.Won)}
              disabled={statusMutation.isPending}
              className="h-10 rounded-cf-md border border-cf-success/40 bg-cf-surface px-3.5 text-[13px] font-semibold text-cf-success hover:bg-cf-success-tint disabled:opacity-50"
            >
              {t(
                restorable
                  ? 'bids.management.actions.restoreWinner'
                  : 'bids.management.actions.assignWinner',
              )}
            </button>
          </div>
        </section>
      )}

      {/* ── Per-bid operator actions — hidden once the bid is decided ───── */}
      {!finished && (
        <section className="rounded-cf-lg border border-cf-line bg-cf-surface px-4 py-4 lg:px-[22px] lg:py-5">
          <h2 className="font-mono text-[10px] tracking-[0.14em] text-cf-ink-subtle uppercase">
            {t('bids.dAdmin.detailPage.operatorSection')}
          </h2>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {/* Owner, 2026-10-11: the outcome exists only once the auction has
                closed. Until then both verbs are visible but disabled, with the
                reason on hover and below — a hidden button explains nothing.
                Owner, 2026-10-12: an OUTBID bid gets neither — it is no longer
                what stands on the auction, so it can be neither won nor lost. */}
            {/* Owner, 2026-10-26: «Виграно» on green, «Не виграно» on the brand burgundy. */}
            {decidable && (
              <>
                <button
                  type="button"
                  onClick={() => void changeStatus(BidStatus.Won)}
                  disabled={statusMutation.isPending || auctionOpen}
                  title={auctionOpen ? t('bids.dAdmin.detailPage.outcomeAfterClose') : undefined}
                  data-testid="bid-mark-won"
                  className="h-10 rounded-cf-md border border-cf-success bg-cf-success px-3.5 text-[13px] font-semibold text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {t('bids.management.actions.markWon')}
                </button>
                <button
                  type="button"
                  onClick={() => void changeStatus(BidStatus.Cancelled)}
                  disabled={statusMutation.isPending || auctionOpen}
                  title={auctionOpen ? t('bids.dAdmin.detailPage.outcomeAfterClose') : undefined}
                  data-testid="bid-mark-not-won"
                  className="h-10 rounded-cf-md border border-cf-brand bg-cf-brand px-3.5 text-[13px] font-semibold text-white hover:bg-cf-brand-hover disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {t('bids.management.actions.markNotWon')}
                </button>
              </>
            )}

            <button
              type="button"
              role="checkbox"
              aria-checked={processed.processed}
              data-testid="bid-processed-toggle"
              onClick={() => void toggleProcessed()}
              disabled={processedMutation.isPending || mirror.busy || processed.derived}
              title={processed.derived ? t('bids.dAdmin.processedDerived') : undefined}
              className={cn(
                'ml-auto inline-flex h-10 items-center gap-1.5 rounded-cf-md px-3.5 font-mono text-[11px] tracking-[0.08em] uppercase',
                processed.derived ? 'cursor-default' : 'disabled:opacity-50',
                processed.processed
                  ? 'bg-cf-success-tint text-cf-success'
                  : 'bg-cf-fill text-cf-ink-muted hover:text-cf-ink',
              )}
            >
              {/* Owner, 2026-10-26: an action until it is done — «Обробити» on grey,
                  then «Оброблено» with the check on green. */}
              {processed.processed && <Check size={13} strokeWidth={2.5} />}
              {t(processed.processed ? 'bids.dAdmin.processedOn' : 'bids.dAdmin.processAction')}
            </button>
          </div>
          {decidable && auctionOpen && (
            <p
              data-testid="bid-outcome-after-close"
              className="mt-2 text-[12px] leading-[1.5] text-cf-ink-subtle"
            >
              {t('bids.dAdmin.detailPage.outcomeAfterClose')}
            </p>
          )}
        </section>
      )}

      {/*
        The relay desk's own section (claim / queue / confirm / cancel) used to
        sit here for every claimable bid. REMOVED 2026-10-11 by owner decision
        — the operator's whole loop on this page is «Оброблено» while the
        auction runs, then «Виграно / Не виграно» once it has closed. The relay
        API and «Угоди → Ретрансляція» are untouched; only this surface is gone.
      */}

      {/*
        Lot-scoped controls. Both act on the CAR, not this single bid, so they
        sit apart from the per-bid status row above; both are audited
        server-side (admin.minimum_bid_set / admin.bids_cancelled).
      */}
      {/* A decided bid's lot controls (minimum price, cancel all) no longer apply. */}
      {lotId && !finished && (
        <>
          <MinimumBidControl carId={lotId} />

          <section className="flex flex-wrap items-center gap-3 rounded-cf-lg border border-cf-line bg-cf-surface px-4 py-4 lg:px-[22px] lg:py-5">
            <div className="min-w-0 flex-1">
              <h2 className="font-mono text-[10px] tracking-[0.14em] text-cf-ink-subtle uppercase">
                {t('bids.dAdmin.detailPage.lotSection')}
              </h2>
              <p className="mt-1.5 text-[13px] text-cf-ink-muted">
                {t('bidDetails.confirmations.cancelAll.message')}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setConfirmCancelAll(true)}
              disabled={cancelAllMutation.isPending}
              className="h-10 shrink-0 rounded-cf-md border border-cf-danger/35 bg-cf-surface px-3.5 text-[13px] font-semibold text-cf-danger hover:bg-cf-danger-tint disabled:opacity-50"
            >
              {cancelAllMutation.isPending
                ? t('bidDetails.loading.cancellingBids')
                : t('bidDetails.actions.cancelAll')}
            </button>
          </section>
        </>
      )}

      {/* ── Every bid on this lot — moved here from the retired lot page ── */}
      {lotId && <LotBidsSection carId={lotId} />}
        </>
      )}

      {/* Mounted only while open, so every opening starts from a clean form. */}
      {mirror.dialog}

      <ConfirmDialog
        open={confirmCancelAll}
        tone="danger"
        title={t('bidDetails.confirmations.cancelAll.title')}
        message={t('bidDetails.confirmations.cancelAll.message')}
        confirmLabel={t('bidDetails.actions.cancelAll')}
        busy={cancelAllMutation.isPending}
        onCancel={() => setConfirmCancelAll(false)}
        onConfirm={() => void cancelAllBids()}
      />

      <ConfirmDialog
        open={replaceWinner !== null}
        tone="danger"
        title={t('bidDetails.confirmations.replaceWinner.title')}
        confirmLabel={t('bidDetails.confirmations.replaceWinner.confirmButton')}
        cancelLabel={t('bidDetails.confirmations.replaceWinner.cancelButton')}
        busy={statusMutation.isPending}
        onCancel={() => setReplaceWinner(null)}
        onConfirm={() => void changeStatus(BidStatus.Won, true)}
        message={
          <>
            <p>
              {t('bidDetails.confirmations.replaceWinner.message', {
                userName: bidderName,
                bidAmount: formatChf(bid.bid),
                existingWinnerCount: replaceWinner?.existingWinners.length ?? 0,
              })}
            </p>
            {replaceWinner && replaceWinner.existingWinners.length > 0 && (
              <ul className="mt-2 list-disc pl-5">
                {replaceWinner.existingWinners.map((winner) => (
                  <li key={winner.bidId}>{formatChf(winner.amount)}</li>
                ))}
              </ul>
            )}
          </>
        }
      />

      {invoiceOpen && detail.deal && clientsQuery.data && rulesQuery.data && (
        <IssueInvoiceDrawer
          clients={clientsQuery.data}
          rules={rulesQuery.data}
          prefill={{ userId: bid.userId, dealId: detail.deal.id, clientName: bidderName }}
          onClose={closeInvoice}
        />
      )}
    </>
  );
}

/**
 * «Ставки на лот · N» — every bid on this bid's lot, in the same table «Угоди»
 * uses, so a row here carries the same actions. Moved from the admin lot page
 * when it was retired (owner, 2026-10-18). All bids are filtered here:
 * `GET /bids` has no per-lot filter, and the list is the payload «Угоди»
 * already caches under the same key.
 */
function LotBidsSection({ carId }: { carId: string }) {
  const bidsQuery = useQuery({ queryKey: BIDS_QUERY_KEY, queryFn: fetchBids });
  const lotBids = (bidsQuery.data ?? []).filter((row) => row.carId === carId);
  return (
    <section className="flex flex-col gap-3" data-testid="bid-detail-lot-bids">
      <h2 className="font-mono text-[10px] tracking-[0.14em] text-cf-ink-subtle uppercase">
        {t('bids.dAdmin.detailPage.lotBidsSection', { count: lotBids.length })}
      </h2>
      {bidsQuery.isLoading ? (
        <p className="rounded-cf-lg border border-cf-line bg-cf-surface px-4 py-10 text-center text-[13px] text-cf-ink-subtle">
          {t('common.loading.data')}
        </p>
      ) : bidsQuery.isError ? (
        <p className="rounded-cf-lg border border-cf-line bg-cf-surface px-4 py-10 text-center text-[13px] text-cf-danger">
          {t('bids.dAdmin.loadError')}
        </p>
      ) : (
        <BidsTable bids={lotBids} />
      )}
    </section>
  );
}

function Field({
  label,
  value,
  mono = false,
  valueClassName,
  testId,
}: {
  label: string;
  value: string;
  mono?: boolean;
  valueClassName?: string;
  testId?: string;
}) {
  return (
    <div className="min-w-0">
      <dt className="font-mono text-[10px] tracking-[0.1em] text-cf-ink-subtle uppercase">
        {label}
      </dt>
      <dd
        className={cn('mt-1 truncate text-sm font-medium', mono && 'font-mono', valueClassName)}
        data-testid={testId}
      >
        {value}
      </dd>
    </div>
  );
}

/**
 * The «Клієнт» fact — a link to the client's own admin page (same target the
 * bids-table row link uses), plus email underneath UNLESS `bidderName` already
 * fell back to it (the same email-dedup rule `BidsTable`/`ClientListItem`
 * apply — showing it twice reads as a bug, not emphasis). The user id is kept,
 * not dropped: support and search need it, so it stays as a small, secondary
 * line under the identity rather than being the only thing shown, which was
 * the complaint this field exists to fix.
 */
function ClientField({
  userId,
  user,
  bidderName,
}: {
  userId: string;
  user: BidUser | null;
  bidderName: string;
}) {
  const emailIsDuplicate = Boolean(user?.email) && bidderName === user?.email;
  return (
    <div className="min-w-0">
      <dt className="font-mono text-[10px] tracking-[0.1em] text-cf-ink-subtle uppercase">
        {t('bids.dAdmin.columns.client')}
      </dt>
      <dd className="mt-1 flex min-w-0 items-start gap-2 text-sm font-medium">
        <UserAvatar
          photoURL={user?.photoURL}
          initials={initialsOf(bidderName)}
          alt=""
          className="mt-0.5 h-8 w-8 shrink-0 text-[11px]"
        />
        <div className="min-w-0">
          <Link
            to="/clients/$id"
            params={{ id: userId }}
            aria-label={t('bids.dAdmin.openClientAria', { bidder: bidderName })}
            className="block truncate rounded-cf-sm text-cf-brand outline-none hover:underline focus-visible:ring-2 focus-visible:ring-cf-brand focus-visible:ring-offset-1"
          >
            {bidderName}
          </Link>
          {!emailIsDuplicate && (
            <div className="truncate text-xs font-normal text-cf-ink-subtle">
              {user?.email ?? '—'}
            </div>
          )}
          <div className="mt-0.5 truncate font-mono text-[10px] font-normal text-cf-ink-faint">
            {t('bids.dAdmin.detailPage.clientId')}: {userId}
          </div>
        </div>
      </dd>
    </div>
  );
}
